import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectGroupGuid,
  selectIncidentGuid,
  useAppSelector,
} from '../../../slices/commonSelectors';
import { fetchRostersForGroups } from '../../../actions/roleActions';
import { IapScope, INCIDENT } from '../ICSConstants';
import { useIncidentRoster } from './incidentRoster/useIncidentRoster';
import { useResourceSets } from './incidentResourceHooks';
import {
  GetIncidentActionResponse,
  useCreateIncidentAction,
  useUpdateIncidentAction,
} from './incidentActionHooks';
import {
  GetOrgActionResponse,
  useCreateOrgAction,
  useUpdateOrgAction,
} from './orgActionHooks';

export type ActionFormValues = {
  name: string;
  description: string;
  poc?: string | null;
  briefed_poc?: boolean;
  status?: string | null;
  start_date?: Date | string | null;
  target_date?: Date | string | null;
  actual_date?: Date | string | null;
  resources?: string[];
  checklist: { label: string; checked: boolean }[];
};

export type ActionEntity = GetIncidentActionResponse | GetOrgActionResponse;

const browserUtcOffset = () => -new Date().getTimezoneOffset();

/**
 * Abstracts the incident-vs-org differences (POC source, resources picker, and
 * which create/update/archive mutations fire) behind one uniform interface so
 * the Action drawer can stay scope-agnostic. Both sides' hooks are instantiated
 * unconditionally (rules of hooks); the unused side stays inert because its
 * queries are `enabled`-gated and its mutations simply aren't invoked.
 */
export const useScopedActionForm = (scope: IapScope) => {
  const isIncident = scope === INCIDENT;
  const dispatch = useDispatch<any>();

  // Incident side
  const incidentId = useAppSelector(selectIncidentGuid);
  const { data: incidentRoster } = useIncidentRoster();
  const { data: allResourceSets } = useResourceSets();
  const createIncident = useCreateIncidentAction();
  const updateIncident = useUpdateIncidentAction();

  // Org side
  const group_guid = useSelector(selectGroupGuid);
  const rosters = useSelector(
    (state: any) => (state.app.rostersForGroups as RosterItem[]) || []
  );
  const createOrg = useCreateOrgAction();
  const updateOrg = useUpdateOrgAction();

  useEffect(() => {
    if (!isIncident && group_guid && rosters.length === 0) {
      dispatch(fetchRostersForGroups([group_guid]));
    }
  }, [dispatch, isIncident, group_guid, rosters.length]);

  const pocOptions = useMemo(() => {
    if (isIncident) {
      return (incidentRoster ?? [])
        .filter((u) => u.userName || u.emailAddress)
        .map((u) => ({
          label: u.userName ?? (u.emailAddress as string),
          value: u.userId,
        }));
    }
    return rosters
      .filter((r) => r.group_guid === group_guid)
      .map((r) => ({ label: r.user_name, value: r.user_guid }));
  }, [isIncident, incidentRoster, rosters, group_guid]);

  const resourceOptions = useMemo(() => {
    if (!isIncident) return undefined;
    return (allResourceSets ?? []).filter(
      (rs) => rs.incidents?.includes(incidentId) && !rs?.archived
    );
  }, [isIncident, allResourceSets, incidentId]);

  const create = async (values: ActionFormValues) => {
    if (isIncident) {
      return createIncident.mutateAsync({ ...values, archived: false } as any);
    }
    return createOrg.mutateAsync({
      ...values,
      utc_offset: browserUtcOffset(),
    } as any);
  };

  const update = async (entity: ActionEntity, values: ActionFormValues) => {
    if (isIncident) {
      const inc = entity as GetIncidentActionResponse;
      return updateIncident.mutateAsync({
        ...values,
        id: inc.id,
        action_id: inc.action_id,
      } as any);
    }
    const org = entity as GetOrgActionResponse;
    return updateOrg.mutateAsync({
      ...values,
      action_id: org.action_id,
      utc_offset: browserUtcOffset(),
      archived: false,
    } as any);
  };

  const archive = async (entity: ActionEntity, archived: boolean) => {
    if (isIncident) {
      const inc = entity as GetIncidentActionResponse;
      return updateIncident.mutateAsync({ ...inc, archived } as any);
    }
    const org = entity as GetOrgActionResponse;
    return updateOrg.mutateAsync({
      action_id: org.action_id,
      name: org.name,
      description: org.description,
      checklist: (org.checklist as { label: string; checked: boolean }[]) ?? [],
      poc: org.poc,
      briefed_poc: org.briefed_poc,
      status: org.status,
      start_date: org.start_date,
      target_date: org.target_date,
      actual_date: org.actual_date,
      utc_offset: browserUtcOffset(),
      archived,
    } as any);
  };

  const isSaving = isIncident
    ? createIncident.isPending || updateIncident.isPending
    : createOrg.isPending || updateOrg.isPending;

  return {
    pocOptions,
    resourceOptions,
    showResources: isIncident,
    create,
    update,
    archive,
    isSaving,
  };
};
